import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tailwind's `lg` breakpoint. The sidebar is an off-canvas drawer below it and
 * a permanent rail at or above it, so "may we collapse to icons?" is exactly
 * "are we at `lg`?".
 */
export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

function getMediaQueryList(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  try {
    return window.matchMedia(query);
  } catch {
    // Some jsdom/matchMedia shims throw on unusual queries. Never let chrome
    // that every page renders die because of a media query.
    return null;
  }
}

/**
 * `matchMedia` as an external store.
 *
 * `useSyncExternalStore` (rather than `useState` + an effect) so the very first
 * render already reports the real viewport -- an effect-based version paints
 * one frame at the wrong width, which on the sidebar reads as a visible flash
 * between the rail and the full-width nav.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = getMediaQueryList(query);
      if (!mql) return () => {};
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
      }
      // Safari < 14 / older shims.
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => getMediaQueryList(query)?.matches ?? false, [query]);

  // Server snapshot: assume mobile-first (no match), matching the CSS defaults.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** True at Tailwind's `lg` and above. */
export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_MEDIA_QUERY);
}
